import React from 'react';
import { gql, useApolloClient, useQuery } from '@apollo/client';
import { AsyncSelect } from '../ReactSelect';
import { ProfileType } from '../Profile/types';
import { getNameLikeCondition } from '../../helpers/search';

export const FETCH_TESTERS = gql`
  query GetPickerTesters(
    $where: JSON
    $profileWhere: JSON
    $profileRequired: Boolean
    $orderBy: [ColumnOrder]
  ) {
    vendors(where: $where, limit: null, orderBy: $orderBy) {
      id
      profileId
      profile(where: $profileWhere, required: $profileRequired) {
        id
        name
        type
      }
      accountNumber
    }
  }
`;

export type Tester = {
  id: string;
  profileId: string;
  profile: {
    id: string;
    name: string;
    type: ProfileType;
  };
  accountNumber?: string;
};

type TesterPickerProps = {
  testerProfileId?: string | null;
  where?: any;
  onChange?: (tester: Tester | null) => void;
  placeholder?: string;
  isClearable?: boolean;
  autoFocus?: boolean;
};

const TesterPicker = ({
  testerProfileId,
  where,
  onChange,
  placeholder,
  isClearable,
  autoFocus,
}: TesterPickerProps): JSX.Element => {
  where = where ?? {};
  const client = useApolloClient();

  const { error: itemError, data: itemData } = useQuery<{
    vendors: Tester[];
  }>(FETCH_TESTERS, {
    variables: {
      where: {
        profileId: testerProfileId,
      },
    },
    skip: !testerProfileId,
  });

  const selected: Tester | undefined = itemData?.vendors[0];

  if (itemError) {
    throw itemError;
  }

  const loadOptionsFunc = async (inputValue: string) => {
    const res = await client.query<{ vendors: Tester[] }>({
      query: FETCH_TESTERS,
      fetchPolicy: 'network-only',
      variables: {
        profileWhere: inputValue
          ? {
              name: getNameLikeCondition(inputValue),
            }
          : undefined,
        profileRequired: !!inputValue,
        where,
        limit: null,
        orderBy: inputValue ? undefined : [{ col: 'profile.name', dir: 'ASC' }],
      },
    });

    if (res.data && res.data.vendors) {
      if (!selected && res.data.vendors.length === 1) {
        onChange?.(res.data.vendors[0]);
      }
      const companies: Tester[] = [];
      const individuals: Tester[] = [];
      res.data.vendors.forEach((tester: Tester) => {
        if (tester.profile.type === 'COMPANY') {
          companies.push(tester);
        } else {
          individuals.push(tester);
        }
      });

      if (companies.length > 0 && individuals.length > 0) {
        return [
          {
            label: 'Individuals',
            options: individuals,
          },
          {
            label: 'Companies',
            options: companies,
          },
        ];
      }

      return res.data.vendors;
    }

    return [];
  };

  return (
    <AsyncSelect
      autoFocus={autoFocus}
      cacheOptions
      value={selected}
      loadOptions={loadOptionsFunc}
      defaultOptions
      isClearable={isClearable}
      noOptionsMessage={() => null}
      menuPlacement="auto"
      styles={{
        menu: (base) => ({
          ...base,
          minWidth: '300px',
        }),
      }}
      onChange={(selected: Tester | null) => {
        if (!selected) {
          onChange?.(null);
        } else {
          onChange?.(selected);
        }
      }}
      placeholder={placeholder ?? 'Enter tester name...'}
      getOptionLabel={(tester) =>
        tester.profile.name +
        (tester.accountNumber ? ` (${tester.accountNumber})` : '')
      }
      getOptionValue={(tester) => tester.profileId}
    />
  );
};

export default TesterPicker;
